import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Check, Edit2, Plus, Trash2, Shield, Award, Crown } from "lucide-react";
import { cn } from "@/lib/utils";

export type Tier = "good" | "better" | "best";

export interface TierLineItem {
  id: string;
  item_name: string;
  description: string | null;
  quantity: number;
  unit_type: string;
  material_cost: number;
  labor_cost: number;
  total_cost: number;
  category: string | null;
  package?: string | null;
}

const TIER_META: Record<Tier, {
  label: string;
  sub: string;
  icon: typeof Shield;
  ring: string;
  headerBg: string;
  iconBg: string;
  pill: string;
}> = {
  good: {
    label: "Good",
    sub: "Builder-grade essentials",
    icon: Shield,
    ring: "border-blue-500/30",
    headerBg: "bg-gradient-to-r from-blue-500/10 via-blue-500/5 to-transparent",
    iconBg: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    pill: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30",
  },
  better: {
    label: "Better",
    sub: "Architectural standard",
    icon: Award,
    ring: "border-emerald-500/40",
    headerBg: "bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent",
    iconBg: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    pill: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  },
  best: {
    label: "Best",
    sub: "Premium / lifetime upgrade",
    icon: Crown,
    ring: "border-amber-500/40",
    headerBg: "bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent",
    iconBg: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    pill: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  },
};

interface EstimateTierStackProps {
  lineItems: TierLineItem[];
  selectedTier: Tier;
  packagePrices?: { good?: number; better?: number; best?: number } | null;
  onSelectTier: (tier: Tier) => void;
  onEditItem: (item: TierLineItem) => void;
  onDeleteItem: (id: string) => void;
  onAddItemToTier: (tier: Tier) => void;
  disabled?: boolean;
}

export const EstimateTierStack = ({
  lineItems,
  selectedTier,
  packagePrices,
  onSelectTier,
  onEditItem,
  onDeleteItem,
  onAddItemToTier,
  disabled,
}: EstimateTierStackProps) => {
  const [openTier, setOpenTier] = useState<Tier | "">(selectedTier || "better");

  const itemsByTier = (tier: Tier) =>
    lineItems.filter((li) => (li.package || "better") === tier);

  return (
    <div className="space-y-4">
      {(Object.keys(TIER_META) as Tier[]).map((tier) => {
        const meta = TIER_META[tier];
        const Icon = meta.icon;
        const items = itemsByTier(tier);
        const isOpen = openTier === tier;
        const isSelected = selectedTier === tier;
        const liveTotal = items.reduce((s, i) => s + Number(i.total_cost || 0), 0);
        const advertisedTotal = Number(packagePrices?.[tier] || 0);
        const total = liveTotal > 0 ? liveTotal : advertisedTotal;

        return (
          <Card
            key={tier}
            className={cn(
              "overflow-hidden border transition-all shadow-sm",
              isSelected
                ? "border-primary shadow-md ring-2 ring-primary/15"
                : meta.ring,
            )}
          >
            <Collapsible open={isOpen} onOpenChange={(o) => setOpenTier(o ? tier : "")}>
              <div className={cn("relative", meta.headerBg)}>
                <div className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center lg:justify-between">
                  <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-3 text-left">
                    <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", meta.iconBg)}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-bold uppercase tracking-wide">
                          {meta.label}
                        </span>
                        {isSelected && (
                          <Badge className="bg-primary text-primary-foreground text-[10px] h-5">
                            <Check className="h-3 w-3 mr-1" /> Selected for proposal
                          </Badge>
                        )}
                        <Badge variant="outline" className={cn("text-[10px] h-5", meta.pill)}>
                          {items.length} {items.length === 1 ? "item" : "items"}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{meta.sub}</div>
                    </div>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                  </CollapsibleTrigger>
                  <div className="flex w-full items-center justify-between gap-3 lg:w-auto lg:justify-end shrink-0">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-foreground tabular-nums leading-none">
                        ${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                        Tier total
                      </div>
                    </div>
                    {!isSelected ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectTier(tier);
                        }}
                      >
                        Use this tier
                      </Button>
                    ) : (
                      <Badge className="bg-primary/10 text-primary border-primary/30 h-8 px-3" variant="outline">
                        <Check className="h-3.5 w-3.5 mr-1" /> Active
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <CollapsibleContent>
                <div className="border-t bg-background p-4 space-y-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-muted-foreground">
                      All quantities, costs, and items are editable.
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onAddItemToTier(tier)}
                      disabled={disabled}
                    >
                      <Plus className="h-4 w-4 mr-2" /> Add item to {meta.label}
                    </Button>
                  </div>
                  {items.length === 0 ? (
                    <div className="text-center py-10 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
                      No items yet for this tier.<br />
                      Click "Generate G/B/B" or add an item above.
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-lg border bg-card">
                      <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/40 hover:bg-muted/40">
                            <TableHead>Item</TableHead>
                            <TableHead className="text-right w-20">Qty</TableHead>
                            <TableHead className="w-20">Unit</TableHead>
                            <TableHead className="text-right w-28">Material</TableHead>
                            <TableHead className="text-right w-28">Labor</TableHead>
                            <TableHead className="text-right w-28">Total</TableHead>
                            <TableHead className="w-20"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map((item) => (
                            <TableRow key={item.id} className="group">
                              <TableCell>
                                <div className="font-medium">{item.item_name}</div>
                                {item.description && (
                                  <div className="text-xs text-muted-foreground">{item.description}</div>
                                )}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{item.unit_type}</TableCell>
                              <TableCell className="text-right tabular-nums">${Number(item.material_cost || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right tabular-nums">${Number(item.labor_cost || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right font-semibold tabular-nums">${Number(item.total_cost || 0).toFixed(2)}</TableCell>
                              <TableCell>
                                <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onEditItem(item)}>
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                    onClick={() => onDeleteItem(item.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-muted/40 hover:bg-muted/40 font-semibold">
                            <TableCell colSpan={5} className="text-right uppercase text-xs tracking-wider text-muted-foreground">Tier Total</TableCell>
                            <TableCell className="text-right text-primary text-base tabular-nums">
                              ${liveTotal.toFixed(2)}
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
};
